import { onBeforeUnmount, type Ref, ref } from "vue";

/**
 * Gravar voz no navegador — a MECÂNICA, sem tela nenhuma.
 *
 * Extraída porque o produto já grava em três lugares (chat público, inbox e
 * agora o compositor dos agentes) e a regra de arquitetura proíbe uma feature
 * de importar a outra: a terceira cópia do mesmo `MediaRecorder` seria a que
 * divergiria primeiro. Aqui vive só o que é igual nos três; cada tela desenha o
 * seu botão.
 *
 * As três decisões herdadas do gravador do chat público, e os porquês:
 *
 * 1. A permissão é pedida NO CLIQUE, nunca no mount — página que levanta o
 *    prompt do microfone ao carregar é página que a pessoa fecha.
 * 2. O mime é ESCOLHIDO, não assumido: Chrome/Firefox dão webm/opus, Safari dá
 *    mp4, e fixar opus faz o Safari subir um arquivo com rótulo errado.
 * 3. As trilhas são PARADAS ao terminar, ou o indicador de microfone fica aceso
 *    na aba depois da gravação — o gesto mais alarmante que uma tela pode ter.
 */

const CANDIDATES = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/mp4"];

export interface VoiceRecorder {
	readonly supported: boolean;
	readonly recording: Ref<boolean>;
	readonly seconds: Ref<number>;
	start(): Promise<void>;
	/** Para e ENTREGA: o `onRecorded` recebe o arquivo. */
	stop(): void;
	/** Para e DESCARTA: nada é entregue. */
	cancel(): void;
}

export function useVoiceRecorder(input: {
	/** Teto da gravação; o padrão são os 3 minutos do chat público. */
	maxSeconds?: number;
	onRecorded: (file: File) => void;
	/** Negado, sem microfone, ou sem suporte — a frase é da tela, não daqui. */
	onFail: () => void;
}): VoiceRecorder {
	const maxSeconds = input.maxSeconds ?? 180;
	const supported =
		typeof window !== "undefined" &&
		typeof MediaRecorder !== "undefined" &&
		!!navigator.mediaDevices?.getUserMedia;

	const recording = ref(false);
	const seconds = ref(0);

	let recorder: MediaRecorder | null = null;
	let stream: MediaStream | null = null;
	let ticker: ReturnType<typeof setInterval> | null = null;

	function pickMime(): string | undefined {
		return CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime));
	}

	function extensionFor(mime: string): string {
		const base = (mime.split(";")[0] ?? "").split("/")[1] ?? "";
		if (base === "mpeg") return "mp3";
		return base.replace(/[^a-z0-9]/g, "") || "bin";
	}

	function releaseStream(): void {
		for (const track of stream?.getTracks() ?? []) track.stop();
		stream = null;
		if (ticker) clearInterval(ticker);
		ticker = null;
	}

	async function start(): Promise<void> {
		if (recording.value) return;
		if (!supported) {
			input.onFail();
			return;
		}
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch {
			input.onFail();
			return;
		}

		const mime = pickMime();
		recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
		const chunks: Blob[] = [];

		recorder.ondataavailable = (event) => {
			if (event.data.size > 0) chunks.push(event.data);
		};
		recorder.onstop = () => {
			releaseStream();
			recording.value = false;
			// O mime do RECORDER, não o candidato: o navegador pode ter escolhido
			// outro, e o arquivo tem de dizer o que tem dentro.
			const type = recorder?.mimeType || chunks[0]?.type || "audio/webm";
			const blob = new Blob(chunks, { type });
			recorder = null;
			if (blob.size === 0) return;
			input.onRecorded(new File([blob], `audio-${Date.now()}.${extensionFor(type)}`, { type }));
		};

		recorder.start();
		recording.value = true;
		seconds.value = 0;
		ticker = setInterval(() => {
			seconds.value += 1;
			if (seconds.value >= maxSeconds) stop();
		}, 1000);
	}

	function stop(): void {
		if (recorder && recorder.state !== "inactive") recorder.stop();
		else {
			releaseStream();
			recording.value = false;
		}
	}

	function cancel(): void {
		// O handler cai ANTES de parar: cancelar não pode entregar o áudio que
		// acabou de descartar.
		if (recorder) recorder.onstop = () => releaseStream();
		if (recorder && recorder.state !== "inactive") recorder.stop();
		else releaseStream();
		recorder = null;
		recording.value = false;
	}

	onBeforeUnmount(() => {
		cancel();
	});

	return { supported, recording, seconds, start, stop, cancel };
}
